import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CustomToastService } from 'src/app/Theme/shared/service/toast.service';
import {TranslateService } from '@ngx-translate/core';
import {
  CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY,
  resolveCategoryLayoutDeviceFromViewport,
} from 'src/app/admin/ad-admin/ad-layout/category-layout.util';
import { parseComparisonMaximumItems, parseComparisonMaximumItemsForDevice } from 'src/app/admin/ad-admin/ad-layout/comparison-layout.util';
import { PortalLayoutResolveService } from 'src/app/admin/ad-admin/ad-layout/portal-layout-resolve.service';
@Injectable({
  providedIn: 'root'
})
export class CompareServiceService {
  public host = environment.apiUrlHost;
  static readonly MAX_PRODUCTS = 5;
  private productsKey = 'products';
  private selectedProductsKey = 'selectedProducts';
  private productsSubject = new BehaviorSubject<any[]>(this.getFromLocalStorage());
  products$ = this.productsSubject.asObservable();
  private configuredMaxProducts = CompareServiceService.MAX_PRODUCTS;
  private comparisonLayoutPayload: Record<string, unknown> | null = null;
  private comparisonLayoutMedia?: MediaQueryList;

  get maxProducts(): number {
    return this.configuredMaxProducts;
  }
  /** Hint banner stays dismissed until the compare list is emptied. */
  private compareHintDismissedSubject = new BehaviorSubject<boolean>(false);
  compareHintDismissed$ = this.compareHintDismissedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private toastService: CustomToastService,
    public translate: TranslateService,
    portalLayoutResolve: PortalLayoutResolveService,
  ) {
    portalLayoutResolve.watchComponent('comparison').subscribe((entry) => {
      this.comparisonLayoutPayload = (entry?.payload as Record<string, unknown> | null) ?? null;
      this.applyMaximumForViewport();
    });
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.comparisonLayoutMedia = window.matchMedia(CATEGORY_LAYOUT_MOBILE_MEDIA_QUERY);
      this.comparisonLayoutMedia.addEventListener('change', () => this.applyMaximumForViewport());
    }
  }

  /** Apply the flexible-screen maximum for the current viewport (desktop or mobile). */
  private applyMaximumForViewport(): void {
    this.applyMaximumItems(
      parseComparisonMaximumItemsForDevice(
        this.comparisonLayoutPayload,
        resolveCategoryLayoutDeviceFromViewport(),
      ),
    );
  }

  /** Apply a flexible-screen maximum (2, 3, 4 or 5). */
  applyMaximumItems(max: number): void {
    const next = parseComparisonMaximumItems({ maximumItems: max });
    if (this.configuredMaxProducts === next) {
      return;
    }
    this.configuredMaxProducts = next;
    this.productsSubject.next([...(this.productsSubject.value ?? [])]);
  }

  searchItemComparison(queryString: string): Observable<any> {
    const url = `${this.host}/CF/cf_itemComper/?${queryString}`;
    return this.http.get(url);
  }

  getItemCode(product: any): string | number | null {
    return product?.General_Information?.item
      ?? product?.General_Information?.Item
      ?? product?.item
      ?? product?._product?.General_Information?.item
      ?? null;
  }

   getFromLocalStorage(): any[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.productsKey) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  getSelectedFromStorage(): any[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(this.selectedProductsKey) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  setToLocalStorage(products: any[]): void {
    if (!products.length) {
      this.compareHintDismissedSubject.next(false);
    }
    this.productsSubject.next(products);
    try {
      localStorage.setItem(this.productsKey, JSON.stringify(products));
    } catch {
      try {
        const slim = products.map((product) => this.toStoredProduct(product, true));
        localStorage.setItem(this.productsKey, JSON.stringify(slim));
      } catch {
        // Keep in-memory compare list even if storage quota is exceeded.
      }
    }
  }

  setSelectedToStorage(products: any[]): void {
    try {
      localStorage.setItem(this.selectedProductsKey, JSON.stringify(products));
    } catch {
      // Ignore quota errors for the selected list.
    }
  }

  updateFavoriteStatus(itemCode: string, isFavorite: boolean): void {
    const products = this.getFromLocalStorage();
    const selectedItems = this.getSelectedFromStorage();
    const updated = products.map(product => {
      if (this.getItemCode(product) === itemCode) {
        product.isFavorite = isFavorite;
      }
      return product;
    });
    const updatedSelected = selectedItems.map(product => {
      if (this.getItemCode(product) === itemCode) {
        product.isFavorite = isFavorite;
      }
      return product;
    });
    this.setToLocalStorage(updated);
    this.setSelectedToStorage(updatedSelected);
  }

  addProduct(product: any): void {
    const itemCode = this.getItemCode(product);
    if (itemCode == null || itemCode === '') {
      return;
    }

    const products = this.productsSubject.value?.length
      ? this.productsSubject.value
      : this.getFromLocalStorage();
    const exists = products.some(p => this.getItemCode(p) === itemCode);
    if (exists){
      this.toastService.showError(this.translate.instant("CFDashboard.AlreadyinCompare"), 'error');
      this.productsSubject.next([...products]);
      return;
    }

    if (products.length >= this.maxProducts) {
      this.toastService.showError(
        this.translate.instant('CFComparison.ListFull', { max: this.maxProducts }),
        'error',
      );
      this.productsSubject.next([...products]);
      return;
    }

    const stored = this.toStoredProduct(product);
    // First product starts the selection. Every later product must share a specification
    // set with the products already chosen — the same rule as the compare page.
    if (products.length > 0 && !this.canCompareWithSelection(stored, products)) {
      this.toastService.showError(this.translate.instant("CFComparison.NotComparable"), 'error');
      this.productsSubject.next([...products]);
      return;
    }

    const updatedProducts = [...products, stored];
    this.setToLocalStorage(updatedProducts);
    this.setSelectedToStorage(updatedProducts);
    this.toastService.showSuccess(this.translate.instant("CFDashboard.AddedtoCompare"), 'Success');
  }


  removeProduct(itemCode: number | string): void {
    const products = this.getFromLocalStorage().filter(p => this.getItemCode(p) !== itemCode);
    this.setToLocalStorage(products);
    const selected = this.getSelectedFromStorage().filter(p => this.getItemCode(p) !== itemCode);
    this.setSelectedToStorage(selected);
  }

  clearAll(): void {
    localStorage.removeItem(this.productsKey);
    localStorage.removeItem(this.selectedProductsKey);
    this.compareHintDismissedSubject.next(false);
    this.productsSubject.next([]);
  }

  dismissCompareHint(): void {
    this.compareHintDismissedSubject.next(true);
  }

  /** First product added to compare; other products are matched against it. */
  getCompareReference(): any | null {
    return this.productsSubject.value?.[0] ?? null;
  }

  /** Specification set keys that hold at least one spec (same rule as the comparison page). */
  getSpecSetKeys(product: any): string[] {
    const specs = product?.Specifications ?? product?._product?.Specifications;
    if (!specs || typeof specs !== 'object') {
      return [];
    }
    return Object.entries(specs)
      .filter(([key, value]) => {
        if (key === 'istable' || value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') {
          const list = (value as { specifications?: unknown[] }).specifications;
          return Array.isArray(list) && list.length > 0;
        }
        return false;
      })
      .map(([key]) => key);
  }

  /** Specification sets shared by every product already in the compare selection. */
  getCommonSpecSetKeys(products: any[] = this.productsSubject.value || []): string[] {
    const sets = (products || []).map((product) => this.getSpecSetKeys(product));
    if (!sets.length || sets.some((keys) => !keys.length)) {
      return [];
    }
    return sets.reduce((common, current) => common.filter((key) => current.includes(key)));
  }

  /**
   * Same rule as the compare page: the first product is always allowed, and each
   * following product must share one specification set with the current selection.
   */
  canCompareWithSelection(product: any, selection: any[] = this.productsSubject.value || []): boolean {
    if (!selection?.length) {
      return true;
    }
    const common = this.getCommonSpecSetKeys(selection);
    if (!common.length) {
      return false;
    }
    return this.getSpecSetKeys(product).some((key) => common.includes(key));
  }

  /** Comparable = shares at least one specification set with the reference product. */
  isComparableWith(product: any, reference: any): boolean {
    return this.canCompareWithSelection(product, reference ? [reference] : []);
  }

  /** Stable sort: products comparable with the current selection come first. */
  sortComparableFirst<T>(products: T[]): T[] {
    const selection = this.productsSubject.value || [];
    if (!selection.length || !this.getCommonSpecSetKeys(selection).length) {
      return products;
    }
    return products
      .map((product, index) => ({ product, index, comparable: this.canCompareWithSelection(product, selection) }))
      .sort((a, b) => (a.comparable === b.comparable ? a.index - b.index : a.comparable ? -1 : 1))
      .map(({ product }) => product);
  }

  private toStoredProduct(product: any, omitSpecs = false): any {
    const gi = product?.General_Information ?? {};
    const images = Array.isArray(product?.Images)
      ? product.Images.slice(0, 2).map((img: any) => ({
          default_image: img?.default_image,
          image_preview: img?.image_preview || img?.image || img?.url
        }))
      : [];

    return {
      General_Information: {
        item: gi.item ?? gi.Item ?? product?.item,
        searchKey1: gi.searchKey1 ?? gi.search_key_1,
        description: gi.description,
        price: gi.price,
        currency: gi.currency
      },
      Images: images,
      Stock_Information: {
        onHand: product?.Stock_Information?.onHand
      },
      Specifications: omitSpecs ? undefined : product?.Specifications,
      isFavorite: !!product?.isFavorite
    };
  }

}
